import React, { useState, useEffect } from 'react';

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'administrator' | 'selger' | 'leder' | 'bruker';
  department: 'salg' | 'markedsfoering' | 'redaksjon' | 'administrasjon' | 'ledelse' | 'web';
  newspaper: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserProfileModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

interface FormErrors {
  [key: string]: string;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ user, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone || '',
    role: user.role,
    department: user.department,
    newspaper: user.newspaper,
    isActive: user.isActive
  });

  // Reset form data when user changes
  useEffect(() => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      department: user.department,
      newspaper: user.newspaper,
      isActive: user.isActive
    });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    setFormErrors({});
  }, [user]);

  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'Fornavn er påkrevd';
    } else if (formData.firstName.length < 2) {
      errors.firstName = 'Fornavn må være minst 2 tegn';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Etternavn er påkrevd';
    } else if (formData.lastName.length < 2) {
      errors.lastName = 'Etternavn må være minst 2 tegn';
    }

    if (!formData.email.trim()) {
      errors.email = 'E-post er påkrevd';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ugyldig e-postformat';
    }

    if (formData.phone && formData.phone.length < 8) {
      errors.phone = 'Telefonnummer må være minst 8 tegn';
    }

    if (!formData.newspaper.trim()) {
      errors.newspaper = 'Avis er påkrevd';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Updating user with data:', formData);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('Brukerinfo oppdatert');
        setIsEditing(false);
        setTimeout(() => {
          onUpdate();
        }, 1000);
      } else {
        const errorData = await response.json();
        console.log('Server error response:', errorData);
        
        // If there are detailed validation errors, show them
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map((err: any) => 
            `${err.field}: ${err.message}`
          ).join(', ');
          throw new Error(`Validation error: ${errorMessages}`);
        }
        
        throw new Error(errorData.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      const errorData = error instanceof Error ? error.message : 'Error updating user';
      
      // Set the error message directly - it should now include detailed validation info
      setError(errorData);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!window.confirm('Er du sikker på at du vil sende passord-reset link til denne brukeren?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${user.id}/password-reset`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('Passord-reset link er sendt');
        
        // Show debug info in development
        if (data.debug) {
          console.log('Password reset link:', data.debug.resetUrl);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send password reset');
      }
    } catch (error) {
      console.error('Error sending password reset:', error);
      setError(error instanceof Error ? error.message : 'Error sending password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Aldri';
    return new Date(dateString).toLocaleDateString('no-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames = {
      administrator: 'Administrator',
      leder: 'Leder',
      selger: 'Selger',
      bruker: 'Bruker'
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  const getDepartmentDisplayName = (department: string) => {
    const departmentNames = {
      salg: 'Salg',
      markedsfoering: 'Markedsføring',
      redaksjon: 'Redaksjon',
      administrasjon: 'Administrasjon',
      ledelse: 'Ledelse',
      web: 'Web'
    };
    return departmentNames[department as keyof typeof departmentNames] || department;
  };

  if (!isOpen) return null;

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-profile-header">
          <div className="user-profile-title">
            <h2>{user.firstName} {user.lastName}</h2>
            <span className="username">@{user.username}</span>
          </div>
          <div className="user-profile-actions">
            {!isEditing && (
              <button
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
                disabled={loading}
              >
                ✏️ Rediger
              </button>
            )}
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="user-profile-content">
          {error && (
            <div className="alert alert-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span>✅ {success}</span>
            </div>
          )}

          {isEditing ? (
            <form onSubmit={handleSubmit} className="user-edit-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="firstName" className="required">Fornavn</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={formErrors.firstName ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.firstName && (
                    <span className="error-message">{formErrors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="required">Etternavn</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={formErrors.lastName ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.lastName && (
                    <span className="error-message">{formErrors.lastName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="required">E-post</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={formErrors.email ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={formErrors.phone ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.phone && (
                    <span className="error-message">{formErrors.phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="role" className="required">Rolle</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="bruker">Bruker</option>
                    <option value="selger">Selger</option>
                    <option value="leder">Leder</option>
                    <option value="administrator">Administrator</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="department" className="required">Avdeling</label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="salg">Salg</option>
                    <option value="markedsfoering">Markedsføring</option>
                    <option value="redaksjon">Redaksjon</option>
                    <option value="administrasjon">Administrasjon</option>
                    <option value="ledelse">Ledelse</option>
                    <option value="web">Web</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="newspaper" className="required">Avis</label>
                  <input
                    type="text"
                    id="newspaper"
                    name="newspaper"
                    value={formData.newspaper}
                    onChange={handleInputChange}
                    className={formErrors.newspaper ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.newspaper && (
                    <span className="error-message">{formErrors.newspaper}</span>
                  )}
                </div>

                <div className="form-group checkbox-group">
                  <label htmlFor="isActive">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    Aktiv bruker
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setFormErrors({});
                    setError(null);
                  }}
                  disabled={loading}
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Lagrer...' : 'Lagre endringer'}
                </button>
              </div>
            </form>
          ) : (
            <div className="user-profile-details">
              <div className="details-grid">
                <div className="detail-item">
                  <label>E-post:</label>
                  <span>{user.email}</span>
                </div>

                {user.phone && (
                  <div className="detail-item">
                    <label>Telefon:</label>
                    <span>{user.phone}</span>
                  </div>
                )}

                <div className="detail-item">
                  <label>Rolle:</label>
                  <span className="role-badge">{getRoleDisplayName(user.role)}</span>
                </div>

                <div className="detail-item">
                  <label>Avdeling:</label>
                  <span className="department-badge">{getDepartmentDisplayName(user.department)}</span>
                </div>

                <div className="detail-item">
                  <label>Avis:</label>
                  <span>{user.newspaper}</span>
                </div>

                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Sist innlogget:</label>
                  <span>{formatDate(user.lastLogin)}</span>
                </div>

                <div className="detail-item">
                  <label>Opprettet:</label>
                  <span>{formatDate(user.createdAt)}</span>
                </div>

                <div className="detail-item">
                  <label>Sist oppdatert:</label>
                  <span>{formatDate(user.updatedAt)}</span>
                </div>
              </div>

              <div className="profile-actions">
                <button
                  className="btn btn-warning"
                  onClick={handlePasswordReset}
                  disabled={loading}
                >
                  🔐 Send passord-reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;